import { useState } from "react";
import { WalletOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { BillOut } from "../../../../../api/bills";
import {
  DEPOSIT_LOAD_PERMISSION,
  DEPOSIT_PAYMENT_PERMISSION,
} from "../../../../../api/deposits";
import { useIsEdgeLocal } from "../../../../../hooks/useEdgeMode";
import { useAppSelector } from "../../../../../store/hooks";
import type { SecondaryAction } from "../OptionsPopover";
import type { DepositInvoiceSource } from "../invoicing/depositInvoice";
import { useOrderingMessage } from "../../useOrderingMessage";
import {
  attachedDepositOf,
  depositActionModeOf,
  depositUseBlockOf,
} from "./billDeposit";
import { DepositChoiceModal } from "./DepositChoiceModal";
import { DepositLoadModal } from "./DepositLoadModal";
import { useBillDepositPicker } from "./useBillDepositPicker";

/**
 * „Депозит" в „Опции" на лентата под масите — ЕДИН бутон (ключът
 * `depositLoad` е записан в личната настройка footerActions — не го сменяй):
 *  • с „Зареждане с депозит" и „Плащане с депозит" — избор „Създай нов
 *    депозит" / „Използвай депозит" (`DepositChoiceModal`);
 *  • само с едното — направо него;
 *  • без нито едно — бутона го няма; в локален режим е заключен (депозитите
 *    са само в облака).
 * „Създай нов депозит" е вземането (`DepositLoadModal`; с „Фактуриране" депозитът с
 * готов бон отваря фактурата за него — `onInvoiceDeposit`). „Използвай
 * депозит" закача депозит към избраната ОТВОРЕНА маса над 0.00
 * (`useBillDepositPicker`) — сменя или маха закачения.
 */
export const useDepositAction = ({
  selectedBill,
  isSelectedBillClosing,
  onInvoiceDeposit,
}: {
  selectedBill: BillOut | null | undefined;
  isSelectedBillClosing: boolean;
  onInvoiceDeposit: (deposit: DepositInvoiceSource) => void;
}) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const permissions = useAppSelector((state) => state.auth.permissions);
  const hasPermission = (name: string) =>
    (permissions ?? []).some((permission) => permission.name === name);
  const mode = depositActionModeOf({
    canLoad: hasPermission(DEPOSIT_LOAD_PERMISSION),
    canUse: hasPermission(DEPOSIT_PAYMENT_PERMISSION),
  });
  const edgeLocal = useIsEdgeLocal();
  const [choiceOpen, setChoiceOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const picker = useBillDepositPicker();

  const attachBlock = depositUseBlockOf(selectedBill, isSelectedBillClosing);
  // Сметка 0.00 със закачен депозит (напр. всичко е преместено с „Раздели"):
  // закачане BE отказва, но изборът остава отворен заради „Махни депозита" —
  // единственият път да се откачи, иначе „Закрий" остава заключен.
  const onlyDetach = attachBlock === "zeroBill" && attachedDepositOf(selectedBill) !== null;
  const attachBlockedReason =
    attachBlock && !onlyDetach ? t(`depositAction.unavailable.${attachBlock}`) : null;

  const openAttachPicker = () => {
    if (!selectedBill || attachBlockedReason) {
      if (attachBlockedReason) message.warning(attachBlockedReason);
      return;
    }
    picker.openFor({ billId: selectedBill.id, tableName: selectedBill.name });
  };

  const action: SecondaryAction | null = mode
    ? {
        key: "depositLoad",
        label: t("ordering.depositLoad"),
        icon: <WalletOutlined />,
        onClick: () => {
          if (mode === "choice") setChoiceOpen(true);
          else if (mode === "load") setLoadOpen(true);
          else openAttachPicker();
        },
        lockedTooltip: edgeLocal ? t("edge.unavailableOffline") : undefined,
      }
    : null;

  const modal = mode ? (
    <>
      <DepositChoiceModal
        open={choiceOpen}
        onClose={() => setChoiceOpen(false)}
        attachBlockedReason={attachBlockedReason}
        tableName={selectedBill?.name ?? null}
        attached={attachedDepositOf(selectedBill)}
        onChoose={(choice) => {
          setChoiceOpen(false);
          if (choice === "load") setLoadOpen(true);
          else openAttachPicker();
        }}
      />
      {mode !== "use" && (
        <DepositLoadModal
          open={loadOpen}
          onClose={() => setLoadOpen(false)}
          onSaved={(deposit, withInvoice) => {
            setLoadOpen(false);
            if (withInvoice) {
              onInvoiceDeposit({
                id: deposit.id,
                amount: deposit.amount,
                paymentMethodId: deposit.paymentMethodId,
                validUntil: deposit.validUntil,
                vatRate: deposit.vatRate,
              });
            }
          }}
        />
      )}
      {picker.modal}
    </>
  ) : null;

  return { action, modal, open: choiceOpen || loadOpen || picker.open };
};
